/**
 * Element context menu — the right-click popover that opens when the user
 * right-clicks an editable element in the iframe preview. Visible part of
 * Phase 3 of the element-insertion engine.
 *
 * The iframe sends `request-element-menu` via postMessage with a payload
 * carrying `instanceId`, `eid`, `rect`, and a flat `ancestorChain`. The
 * bridge re-emits as `iframe:request-element-menu`. This module subscribes,
 * resolves the relevant pattern adapter via `resolveAdapter(chainProbe(chain))`,
 * and opens an `openPopoverMenu` anchored at the click rect.
 *
 * Menu items are derived from the adapter's `menuActions` array. Clicking an
 * item emits `element-menu:<action>` on the event bus with the relevant
 * payload + adapter kind — Phase 4 attaches the actual mutation handlers.
 *
 * Positioning: the iframe-supplied rect is in iframe viewport coordinates.
 * We add the parent iframe element's `getBoundingClientRect()` to translate
 * to parent window coordinates, then place a hidden 1×1 synthetic trigger
 * there. `openPopoverMenu` anchors to that trigger's rect.
 */

import { eventBus } from '../core/events.js';
import { getAdapterByKind } from '../editing/pattern-adapters.js';
import { resolveAdapterKindForPayload } from '../editing/adapters/freeform-block-adapter.js';
import { escapeForAttrSelector } from '../editing/editable-utils.js';
import { resolveSectionByInstance } from '../editing/section-utils.js';
import { hasClipboard } from '../editing/element-clipboard.js';
import { isPaintModeActive } from '../editing/format-painter.js';
import {
  bulkDeleteSelectedElements,
  bulkDuplicateSelectedElements,
  collapseElementSelection,
  elementSelection,
  elementSelectionSize,
  elementUnitKey,
  resolveElementUnit,
  selectSiblingGroup
} from '../editing/element-selection.js';
import { openPopoverMenu } from './popover-menu.js';

let _initialized = false;
let _closeFn = null;
let _trigger = null;

const ACTION_LABELS = {
  'add-after': (adapter) => `Add ${adapter?.label?.toLowerCase() || 'element'} after`,
  'move-prev': (adapter) => (adapter?.reorderAxis === 'y' ? 'Move up' : 'Move left'),
  'move-next': (adapter) => (adapter?.reorderAxis === 'y' ? 'Move down' : 'Move right'),
  duplicate: () => 'Duplicate',
  copy: () => 'Copy',
  cut: () => 'Cut',
  paste: () => 'Paste',
  'copy-style': () => 'Copy style',
  'paste-style': () => 'Paste style',
  'toggle-visibility': (adapter, payload) => (_isHidden(payload, adapter) ? 'Show' : 'Hide'),
  'add-ai-context': () => 'Add to AI chat',
  // EA-9: opens the element's glass panel, where the entrance row lives.
  entrance: () => 'Entrance animation…',
  delete: () => 'Delete'
};

const GENERIC_ACTIONS = ['duplicate', 'entrance', 'toggle-visibility', 'delete'];

const CLIPBOARD_ACTIONS = ['copy', 'cut', 'paste'];

const PAINTER_ACTIONS = ['copy-style', 'paste-style'];

/**
 * Subscribe to iframe:request-element-menu. Idempotent.
 */
export function initElementContextMenu() {
  if (_initialized) return;
  _initialized = true;
  eventBus.on('iframe:request-element-menu', _onRequest);
}

export function destroyElementContextMenu() {
  if (!_initialized) return;
  eventBus.off('iframe:request-element-menu', _onRequest);
  _initialized = false;
  _closeActiveMenu();
}

function _closeActiveMenu() {
  if (typeof _closeFn === 'function') {
    try {
      _closeFn();
    } catch {
      /* noop */
    }
  }
  _closeFn = null;
  if (_trigger && _trigger.parentNode) {
    _trigger.parentNode.removeChild(_trigger);
  }
  _trigger = null;
}

function _onRequest(payload) {
  if (!payload || !payload.instanceId || !payload.eid) return;
  // One shared resolver (freeform fallback included) — never duplicated here.
  const adapter = getAdapterByKind(resolveAdapterKindForPayload(payload));
  openElementContextMenu(payload, adapter);
}

/**
 * Open the context menu for an element. Public so tests/devtools can call it
 * directly without going through the iframe round-trip.
 *
 * @param {object} payload — { instanceId, eid, rect, ancestorChain, ... }
 * @param {object|null} adapter — resolved pattern adapter (or null for generic fallback)
 */
export function openElementContextMenu(payload, adapter) {
  _closeActiveMenu();

  const items = _buildItems(payload, adapter);
  if (items.length === 0) return;

  _trigger = _makeSyntheticTrigger(payload.point || payload.rect);
  if (!_trigger) return;

  _closeFn = openPopoverMenu(_trigger, {
    items,
    align: 'left',
    menuLabel: adapter ? `${adapter.label} actions` : 'Element actions',
    consumeCanvasDismiss: true,
    onClose: () => {
      _closeFn = null;
      if (_trigger && _trigger.parentNode) _trigger.parentNode.removeChild(_trigger);
      _trigger = null;
    }
  });
}

/**
 * How many units this menu acts on (16C1). Greater than 1 only when a
 * multi-selection exists AND the right-clicked unit is a member of it —
 * right-clicking outside the selection acts singly, and the click that opened
 * the menu has already collapsed the selection to that unit.
 */
function _bulkCountFor(resolved) {
  const size = elementSelectionSize();
  if (size < 2 || !resolved) return 1;
  return elementSelection.isSelected(elementUnitKey(resolved.instanceId, resolved.unitEid))
    ? size
    : 1;
}

/**
 * "Select all N in this group" — the discoverable path to a sibling
 * multi-selection. Modifier clicks teach nobody that the feature exists; this
 * item does. Omitted when the unit has no siblings to gather.
 */
function _selectGroupItem(payload, adapter, resolved) {
  if (!resolved || resolved.units.length < 2) return null;
  const noun = adapter?.label ? `${adapter.label.toLowerCase()}s` : 'items';
  return {
    id: 'select-group',
    label: `Select all ${resolved.units.length} ${noun} in this group`,
    onSelect: () => {
      selectSiblingGroup(payload, adapter ? adapter.kind : null);
    }
  };
}

/**
 * Build the menu's items. Exported so the bulk-vs-single rules can be asserted
 * behaviourally (what a label says, what an onSelect actually does) without
 * mounting a popover — same convention as `canReorderInPlace` above.
 */
export function buildElementMenuItems(payload, adapter) {
  return _buildItems(payload, adapter);
}

function _buildItems(payload, adapter) {
  const movePosition = adapter?.reorderable ? reorderPosition(payload, adapter) : null;
  const baseActions =
    adapter && Array.isArray(adapter.menuActions) && adapter.menuActions.length > 0
      ? adapter.menuActions
      : GENERIC_ACTIONS;
  const actions = _withMoveActions(
    _withAiContextAction(
      _withPainterActions(
        _withClipboardActions(_withEntranceAction(_withVisibilityAction(baseActions), payload))
      )
    ),
    adapter,
    payload,
    movePosition
  );

  const adapterKind = adapter ? adapter.kind : null;
  const noun = adapter?.label ? `${adapter.label.toLowerCase()}s` : 'items';
  // Resolved ONCE: each call re-parses the whole section HTML, and the count,
  // the membership test and the group item all want the same answer.
  const resolved = resolveElementUnit(payload, adapterKind);
  const bulkCount = _bulkCountFor(resolved);

  const items = [];
  for (const action of actions) {
    const labelFn = ACTION_LABELS[action];
    if (!labelFn) continue;
    if (action === 'paste' && !hasClipboard()) continue;
    // Paste style is only meaningful while a Copy style snapshot is active.
    if (action === 'paste-style' && !isPaintModeActive()) continue;

    // The two actions that go bulk. Everything else stays single-target, and
    // COLLAPSES the selection to the clicked unit before running — the 16A1
    // rule applied here so no entry point acquires bulk semantics by accident.
    if (bulkCount > 1 && (action === 'duplicate' || action === 'delete')) {
      items.push({
        id: action,
        label: `${labelFn(adapter, payload)} ${bulkCount} ${noun}`,
        danger: action === 'delete',
        onSelect: () => {
          if (action === 'delete') bulkDeleteSelectedElements();
          else bulkDuplicateSelectedElements();
        }
      });
      continue;
    }

    const disabledReason =
      action === 'move-prev' && movePosition?.index === 0
        ? 'Already first'
        : action === 'move-next' && movePosition?.index === movePosition?.count - 1
          ? 'Already last'
          : null;
    items.push({
      id: action,
      disabled: Boolean(disabledReason),
      disabledReason,
      label: labelFn(adapter, payload),
      danger: action === 'delete',
      onSelect: () => {
        if (bulkCount > 1) collapseElementSelection(`single-target:${action}`);
        eventBus.emit(`element-menu:${action}`, { payload, adapterKind });
      }
    });
  }

  const selectGroup = _selectGroupItem(payload, adapter, resolved);
  if (selectGroup && bulkCount < 2) items.unshift(selectGroup);
  return items;
}

function _withMoveActions(actions, adapter, payload, position = reorderPosition(payload, adapter)) {
  // Reorderable kinds get "Move left/right" (or up/down for a y-axis kind) at
  // the top of the menu — the keyboard-free path to the same reorder commit the
  // drag gesture produces. Non-reorderable adapters are unaffected.
  if (!adapter?.reorderable) return actions;
  if (position?.allowed === false) return actions;
  return ['move-prev', 'move-next', ...Array.from(actions || [])];
}

/**
 * A kind can be `reorderable` while THIS element's markup is a structural
 * variant the mover doesn't support — e.g. the social adapter matches any
 * `.cs-social-link` but only reorders the direct-anchor form
 * (`.cs-social > a.cs-social-link`), not the wrapped `ul.cs-social > li > a`
 * dumps. The mover filters siblings with `reorderSiblingSelector`, so mirror
 * that here: only offer Move items when the element's repeat root would
 * actually be picked up. Unresolvable states fail open (keep the items) so a
 * transient lookup miss never hides a working control. Exported for tests.
 */
export function canReorderInPlace(payload, adapter) {
  return reorderPosition(payload, adapter)?.allowed !== false;
}

/** Match moveAdjacentSiblingPreservingEids: closest move root, then filtered direct siblings. */
function reorderPosition(payload, adapter) {
  const itemSelector = adapter?.reorderItemSelector || adapter?.itemSelector;
  const siblingSelector =
    adapter?.reorderSiblingSelector || adapter?.reorderSelector || itemSelector;
  if (!siblingSelector || !itemSelector || !payload?.instanceId || !payload?.eid) return null;
  if (typeof DOMParser === 'undefined') return null;
  const sectionHtml = resolveSectionByInstance(payload.instanceId)?.section?.html;
  if (!sectionHtml) return null;
  try {
    const doc = new DOMParser().parseFromString(`<div>${sectionHtml}</div>`, 'text/html');
    const root = doc.body.firstElementChild;
    const selected = root?.querySelector(`[data-pb-eid="${escapeForAttrSelector(payload.eid)}"]`);
    const item = selected?.closest(itemSelector);
    if (!item?.parentElement || !item.matches(siblingSelector)) return { allowed: false };
    const siblings = [...item.parentElement.children].filter((child) =>
      child.matches(siblingSelector)
    );
    return { allowed: true, index: siblings.indexOf(item), count: siblings.length };
  } catch {
    return null;
  }
}

function _withClipboardActions(actions) {
  const list = Array.from(actions || []);
  // Inject Copy / Cut / Paste right after Duplicate so the clipboard menu
  // sits with the other in-place clones; otherwise insert at the top.
  const insertAt = list.indexOf('duplicate');
  const tail = list.slice(insertAt === -1 ? 0 : insertAt + 1);
  const head = insertAt === -1 ? [] : list.slice(0, insertAt + 1);
  return [...head, ...CLIPBOARD_ACTIONS, ...tail];
}

function _withPainterActions(actions) {
  const list = Array.from(actions || []);
  // Slot the painter actions after Paste so the clipboard group reads as
  // Copy / Cut / Paste / Copy style / Paste style. Falls back to the start
  // when no `paste` anchor exists.
  const insertAt = list.indexOf('paste');
  if (insertAt === -1) return [...PAINTER_ACTIONS, ...list];
  const head = list.slice(0, insertAt + 1);
  const tail = list.slice(insertAt + 1);
  return [...head, ...PAINTER_ACTIONS, ...tail];
}

function _withVisibilityAction(actions) {
  const list = Array.from(actions || []);
  if (list.includes('toggle-visibility')) return list;
  const deleteIndex = list.indexOf('delete');
  if (deleteIndex === -1) {
    list.push('toggle-visibility');
  } else {
    list.splice(deleteIndex, 0, 'toggle-visibility');
  }
  return list;
}

/** Every editable PAGE element gets the entrance doorway, regardless of the
 * adapter's own action list. Shared nav/footer are intentionally excluded from
 * entrance v1, so remove a legacy/generic occurrence there as well. */
export function _withEntranceAction(actions, payload) {
  const list = Array.from(actions || []);
  const resolved = resolveSectionByInstance(payload?.instanceId);
  const isPageSection = resolved?.sectionPath?.startsWith('pages.');
  if (!isPageSection) return list.filter((action) => action !== 'entrance');
  if (list.includes('entrance')) return list;
  const anchor = list.indexOf('toggle-visibility');
  const insertAt = anchor === -1 ? list.indexOf('delete') : anchor;
  if (insertAt === -1) list.push('entrance');
  else list.splice(insertAt, 0, 'entrance');
  return list;
}

/**
 * "Add to AI chat" sits just above Delete — last in the safe group, so it is
 * easy to reach without neighbouring the destructive item's hover target.
 * Exported for tests.
 */
export function _withAiContextAction(actions) {
  const list = Array.from(actions || []);
  if (list.includes('add-ai-context')) return list;
  const deleteIndex = list.indexOf('delete');
  if (deleteIndex === -1) list.push('add-ai-context');
  else list.splice(deleteIndex, 0, 'add-ai-context');
  return list;
}

function _isHidden(payload, adapter) {
  if (!payload?.instanceId || !payload?.eid) return false;
  const resolved = resolveSectionByInstance(payload.instanceId);
  const elements = resolved?.section?.overrides?.elements;
  if (!elements) return false;
  const eid = _visibilityTargetEid(resolved.section.html, payload.eid, adapter);
  return !!(eid && elements[eid]?.hidden === true);
}

function _visibilityTargetEid(sectionHtml, selectedEid, adapter) {
  if (!sectionHtml || !selectedEid) return selectedEid || null;
  const itemSelector = adapter?.itemSelector;
  if (!itemSelector) return selectedEid;
  if (typeof DOMParser === 'undefined') return selectedEid;
  try {
    const doc = new DOMParser().parseFromString(`<div>${sectionHtml}</div>`, 'text/html');
    const root = doc.body.firstElementChild;
    const selected = root?.querySelector(`[data-pb-eid="${escapeForAttrSelector(selectedEid)}"]`);
    const item = selected?.closest(itemSelector);
    return item?.getAttribute('data-pb-eid') || selectedEid;
  } catch {
    return selectedEid;
  }
}

function _makeSyntheticTrigger(rect) {
  const iframe = document.getElementById('pb-preview');
  if (!iframe) return null;
  const iframeRect = iframe.getBoundingClientRect();

  const r = rect || { top: 0, left: 0, width: 0, height: 0 };
  const left = iframeRect.left + Number(r.left || 0);
  const top = iframeRect.top + Number(r.top || 0);

  const btn = document.createElement('button');
  btn.type = 'button';
  btn.setAttribute('aria-hidden', 'true');
  btn.tabIndex = -1;
  btn.style.position = 'fixed';
  btn.style.left = `${left}px`;
  btn.style.top = `${top}px`;
  btn.style.width = '1px';
  btn.style.height = '1px';
  btn.style.padding = '0';
  btn.style.margin = '0';
  btn.style.border = '0';
  btn.style.background = 'transparent';
  btn.style.opacity = '0';
  btn.style.pointerEvents = 'none';
  document.body.appendChild(btn);
  return btn;
}
